import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, CheckCircle2, Loader2, Gift } from "lucide-react";
import publicApi from "../../api/public";

const STORAGE_KEY = "tpco_exit_popup_seen_v1";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ nom: "", telephone: "", email: "" });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const shown = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const onMouseLeave = (e) => {
      if (shown.current) return;
      // Detect leave through top edge
      if (e.clientY < 10) {
        shown.current = true;
        sessionStorage.setItem(STORAGE_KEY, "1");
        setOpen(true);
      }
    };
    // Fallback mobile : après 30s d'inactivité
    let inactivity;
    const reset = () => {
      clearTimeout(inactivity);
      inactivity = setTimeout(() => {
        if (!shown.current) {
          shown.current = true;
          sessionStorage.setItem(STORAGE_KEY, "1");
          setOpen(true);
        }
      }, 30000);
    };
    reset();
    ["mousemove", "scroll", "touchstart"].forEach((ev) => window.addEventListener(ev, reset));
    document.addEventListener("mouseout", onMouseLeave);
    return () => {
      document.removeEventListener("mouseout", onMouseLeave);
      ["mousemove", "scroll", "touchstart"].forEach((ev) => window.removeEventListener(ev, reset));
      clearTimeout(inactivity);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      await publicApi.post("/contact", {
        nom: form.nom,
        telephone: form.telephone,
        email: form.email,
        message: "Demande de brochure depuis la pop-up d'intention de sortie.",
        type: "contact",
      });
      setSent(true);
    } catch (_e) {
      setErr("Erreur d'envoi, réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="exit-popup"
        >
          <motion.div
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-[#0F1A3D] flex items-center justify-center shadow"
              aria-label="Fermer"
              data-testid="exit-popup-close"
            >
              <X className="w-4 h-4" />
            </button>
            {/* Header coloré */}
            <div className="bg-gradient-to-br from-[#0F1A3D] via-[#1E3A8A] to-[#E91E63] p-6 lg:p-8 text-white relative overflow-hidden">
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-white/10 rounded-full" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur rounded-full text-[10px] uppercase tracking-[0.2em] font-bold mb-3">
                  <Gift className="w-3.5 h-3.5" /> Offre limitée
                </div>
                <h3 className="font-display text-2xl lg:text-3xl font-extrabold leading-tight tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Avant de partir...
                </h3>
                <p className="text-white/85 text-sm mt-2 leading-relaxed">
                  Recevez gratuitement notre <strong>brochure complète 2026</strong> — projets, prix et plans détaillés.
                </p>
              </div>
            </div>

            <div className="p-6 lg:p-8">
              {sent ? (
                <div className="text-center py-4" data-testid="exit-popup-sent">
                  <div className="w-14 h-14 rounded-full bg-[#E5F8E9] text-[#2D7A3F] flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h4 className="font-display text-lg font-bold text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>Brochure en route</h4>
                  <p className="text-sm text-[#4B5775] mt-1">Vous la recevrez sous 24h. Merci !</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="exit-popup-form">
                  <input
                    required
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    placeholder="Votre nom *"
                    className="w-full h-11 px-3 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                  />
                  <input
                    required
                    type="tel"
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    placeholder="Téléphone * (+221 77…)"
                    className="w-full h-11 px-3 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email *"
                    className="w-full h-11 px-3 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                  />
                  {err && <div className="text-xs text-[#C83B3B]">{err}</div>}
                  <button type="submit" disabled={sending} className="w-full h-12 rounded-lg bg-[#E91E63] hover:bg-[#D81B60] text-white font-bold text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2 transition disabled:opacity-50">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Recevoir la brochure</>}
                  </button>
                  <p className="text-[10px] text-[#4B5775] text-center pt-1">Aucun spam · RGPD · Désinscription en 1 clic</p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
